import { Directive, ElementRef, HostListener } from '@angular/core';
import { BRAND } from '../../data/brand/brand.data';

const MAINTENANCE_SRC = BRAND.logo.maintenance;

/**
 * Replaces a broken <img> with the Codigrate maintenance illustration, framed
 * in a glass box ( the global `.img-fallback` class ), so missing screenshots
 * or links never render as a broken image.
 *
 * The element is reused across carousels ( the home / theme platform sliders
 * swap the bound `src` on the same <img> ), so the fallback state must reset
 * per source: a successfully loaded real image clears the glass box, while the
 * maintenance image keeps it. The data flag stops a failing fallback looping.
 *
 * Usage: add the `appImgFallback` attribute to any <img>.
 */
@Directive( {
  selector: 'img[appImgFallback]',
} )
export class ImgFallbackDirective {

  constructor( private readonly el: ElementRef<HTMLImageElement> ) {}

  @HostListener( 'error' )
  onError(): void {
    const img = this.el.nativeElement;

    // The maintenance image itself failed to load: stop here so we never loop.
    // Guarding on the current source ( not a sticky data flag ) is what lets a
    // SECOND broken source - e.g. switching to another platform whose screenshot
    // is also missing - fall back again instead of rendering a broken image.
    if ( this.isMaintenance( img ) ) {
      return;
    }

    img.classList.add( 'img-fallback' );
    img.src = MAINTENANCE_SRC;
  }

  @HostListener( 'load' )
  onLoad(): void {
    const img = this.el.nativeElement;

    // Keep the glass box when our own maintenance image is what loaded.
    if ( this.isMaintenance( img ) ) {
      return;
    }

    // A real image loaded ( the carousel / platform switcher may have swapped in
    // a new, valid source ): drop any leftover fallback framing so it shows
    // normally and can fall back again later if a future source is broken.
    img.classList.remove( 'img-fallback' );
  }

  private isMaintenance( img: HTMLImageElement ): boolean {
    // The maintenance asset is a .webp; matching the bare filename here ( not a
    // hard-coded extension ) keeps the glass box + caption on the fallback image
    // instead of stripping them the instant it loads.
    return img.src.includes( 'maintenance' );
  }
}
